import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithGoogle,
  signInWithMicrosoft,
  isSyllySyncDomain,
  isEduDomain,
  isSyllySyncDevDomain,
  getEmailDomain,
  getOrganization,
  getUniversityForDomain,
  getAccessibleOrganizations,
  createOnCampusOrgUnderUniversity,
  isOrgAdmin,
  createUniversityOrganization,
  hasPendingMemberRequest,
  hasApprovedMemberRequest,
  createMemberRequest
} from '../services/authService';
import { PENDING_BILLING_MODAL_STORAGE_KEY, SELECTED_ORG_STORAGE_KEY } from '../contexts/AuthContext';
import OrgTypeDialog from '../components/OrgTypeDialog';
import OrgRequestDialog from '../components/OrgRequestDialog';
import OrgPickerDialog from '../components/OrgPickerDialog';
import UniversityExistsDialog from '../components/UniversityExistsDialog';
import EduDomainDialog from '../components/EduDomainDialog';
import { isCommonEmailDomain, normalizeDomain } from '../utils/domainUtils';
import './Login.css';

const Login = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOrgTypeDialog, setShowOrgTypeDialog] = useState(false);
  const [showOrgRequestDialog, setShowOrgRequestDialog] = useState(false);
  const [showOrgPicker, setShowOrgPicker] = useState(false);
  const [showUniversityExistsDialog, setShowUniversityExistsDialog] = useState(false);
  const [showEduDomainDialog, setShowEduDomainDialog] = useState(false);
  const [accessibleOrgs, setAccessibleOrgs] = useState([]);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [pendingOrgType, setPendingOrgType] = useState(null);
  const [pendingUniversityDomain, setPendingUniversityDomain] = useState(null);
  const navigate = useNavigate();

  const persistSelectedOrg = (orgId) => {
    try {
      localStorage.setItem(SELECTED_ORG_STORAGE_KEY, orgId);
    } catch (e) {
      // ignore
    }
  };

  const persistAndRedirectToOrg = (orgId) => {
    persistSelectedOrg(orgId);
    window.location.href = '/org-dashboard/signups';
  };

  const redirectToDashboardWithBillingModal = (orgId) => {
    persistSelectedOrg(orgId);
    try {
      localStorage.setItem(PENDING_BILLING_MODAL_STORAGE_KEY, orgId);
    } catch (e) {
      // ignore
    }
    window.location.href = '/org-dashboard/grades';
  };

  const handleEduUser = async (user, email) => {
    const domain = normalizeDomain(getEmailDomain(email));
    const canUseEmailDomain = domain && !isCommonEmailDomain(domain);
    if (!domain) {
      setError('Invalid email domain.');
      return;
    }

    const orgs = await getAccessibleOrganizations(user.uid);
    if (orgs.length > 0) {
      if (orgs.length === 1) {
        persistAndRedirectToOrg(orgs[0].id);
        return;
      }
      setAccessibleOrgs(orgs);
      setShowOrgPicker(true);
      return;
    }

    const university = canUseEmailDomain ? await getUniversityForDomain(domain) : null;
    if (university) {
      setPendingEmail(email);
      setPendingUserId(user.uid);
      setPendingUniversityDomain(domain);
      setShowUniversityExistsDialog(true);
      return;
    }

    const org = canUseEmailDomain ? await getOrganization(domain) : null;
    if (org) {
      const isAdmin = await isOrgAdmin(user.uid, domain);
      if (isAdmin) {
        persistAndRedirectToOrg(domain);
        return;
      }
      const hasApproved = await hasApprovedMemberRequest(user.uid, domain);
      if (hasApproved) {
        persistAndRedirectToOrg(domain);
        return;
      }
      const hasPending = await hasPendingMemberRequest(user.uid, domain);
      if (hasPending) {
        setError('Your request to join this organization is pending approval. You will receive an email once approved.');
        return;
      }
      const name = prompt('Please enter your name to request access to this organization:');
      if (name) {
        try {
          await createMemberRequest(user.uid, email, domain, name);
          setError('');
          alert('Your request has been submitted. You will receive access once approved by an organization administrator.');
        } catch (err) {
          console.error('Error creating member request:', err);
          setError('Failed to create request. Please try again.');
        }
      }
      return;
    }

    setPendingEmail(email);
    setPendingUserId(user.uid);

    if (isSyllySyncDevDomain(email)) {
      // Internal development accounts always create a full, billing-exempt organization.
      setPendingUniversityDomain(domain);
      setPendingOrgType('university');
      setShowOrgRequestDialog(true);
    } else if (isEduDomain(email)) {
      // .edu sign-ins automatically create under their own .edu domain.
      setPendingUniversityDomain(domain);
      setShowOrgTypeDialog(true);
    } else {
      // Other sign-ins choose an organization-owned domain; common mailbox domains are rejected in the dialog.
      setPendingUniversityDomain(canUseEmailDomain ? domain : null);
      setShowEduDomainDialog(true);
    }
  };

  const handleOrgPickerSelect = (orgId) => {
    setShowOrgPicker(false);
    persistAndRedirectToOrg(orgId);
  };

  const handleCreateOnCampusUnderUniversity = async (orgName, requestedByName) => {
    const domain = pendingUniversityDomain || normalizeDomain(getEmailDomain(pendingEmail));
    try {
      const { orgId } = await createOnCampusOrgUnderUniversity(
        pendingUserId,
        pendingEmail,
        domain,
        orgName,
        requestedByName
      );
      setShowUniversityExistsDialog(false);
      setPendingEmail(null);
      setPendingUserId(null);
      setPendingUniversityDomain(null);
      setError('');
      persistAndRedirectToOrg(orgId);
    } catch (err) {
      console.error('Error creating on-campus org:', err);
      setError(err.message || 'Failed to create organization. Please try again.');
    }
  };

  const handleRequestAccessToUniversity = async () => {
    const domain = pendingUniversityDomain || normalizeDomain(getEmailDomain(pendingEmail));
    const name = prompt('Please enter your name to request access to the university account:');
    if (!name) return;
    try {
      await createMemberRequest(pendingUserId, pendingEmail, domain, name);
      setShowUniversityExistsDialog(false);
      setPendingEmail(null);
      setPendingUserId(null);
      setPendingUniversityDomain(null);
      setError('');
      alert('Your request has been submitted. You will receive access once approved by an organization administrator.');
    } catch (err) {
      console.error('Error creating member request:', err);
      setError('Failed to create request. Please try again.');
    }
  };

  const handleOrgTypeSelection = (orgType) => {
    setShowOrgTypeDialog(false);
    setPendingOrgType(orgType);
    setShowOrgRequestDialog(true);
  };

  const handleEduDomainSubmit = async (organizationDomain) => {
    setShowEduDomainDialog(false);

    setPendingUniversityDomain(organizationDomain);
    setShowOrgTypeDialog(true);
  };

  const handleOrgDetailsSubmit = async (orgName, creatorName) => {
    setShowOrgRequestDialog(false);

    try {
      // Use the selected organization domain, or the signed-in .edu domain when available.
      const domain = pendingUniversityDomain || normalizeDomain(getEmailDomain(pendingEmail));

      if (pendingOrgType === 'university') {
        // Auto-create university organization
        const { orgId } = await createUniversityOrganization(
          pendingUserId,
          pendingEmail,
          domain,
          orgName,
          creatorName
        );

        // Cloud Function will automatically send notification to admins
        // when organization document is created

        setError('');
        if (isSyllySyncDevDomain(pendingEmail)) {
          persistSelectedOrg(orgId);
          window.location.href = '/org-dashboard/grades';
        } else {
          redirectToDashboardWithBillingModal(orgId);
        }
      } else {
        // For on-campus org, check if university exists first
        const university = await getUniversityForDomain(domain);
        if (university) {
          // Create on-campus org under existing university
          const { orgId } = await createOnCampusOrgUnderUniversity(
            pendingUserId,
            pendingEmail,
            domain,
            orgName,
            creatorName
          );

          // Cloud Function will automatically send notification to admins
          // when organization document is created

          setError('');
          alert('On-campus organization created successfully! It is funded by your university.');
          persistAndRedirectToOrg(orgId);
        } else {
          // University doesn't exist - show error
          setError('No university found for this domain. Please create a university organization first or ensure the domain is correct.');
          setShowOrgTypeDialog(true);
          return;
        }
      }
    } catch (err) {
      console.error('Error creating organization:', err);
      setError(err.message || 'Failed to create organization. Please try again.');
    }

    setPendingEmail(null);
    setPendingUserId(null);
    setPendingOrgType(null);
    setPendingUniversityDomain(null);
  };

  const handleOrgDetailsCancel = () => {
    setShowOrgRequestDialog(false);
    setPendingEmail(null);
    setPendingUserId(null);
    setPendingOrgType(null);
    setPendingUniversityDomain(null);
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await signInWithGoogle();
      const email = result.user.email;

      if (isSyllySyncDomain(email)) {
        navigate('/dashboard');
      } else if (isEduDomain(email)) {
        await handleEduUser(result.user, email);
      } else {
        // Non-.edu email users choose an organization-owned domain before creating a new org.
        await handleEduUser(result.user, email);
      }
    } catch (err) {
      console.error('Google sign-in error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled.');
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with this email using a different sign-in method. Please try signing in with Microsoft instead.');
      } else {
        setError('Failed to sign in with Google. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await signInWithMicrosoft();
      const email = result.user.email;

      if (isSyllySyncDomain(email)) {
        navigate('/dashboard');
      } else if (isEduDomain(email)) {
        await handleEduUser(result.user, email);
      } else {
        // Non-.edu email users choose an organization-owned domain before creating a new org.
        await handleEduUser(result.user, email);
      }
    } catch (err) {
      console.error('Microsoft sign-in error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled.');
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with this email using Google sign-in. Please sign in with Google instead.');
      } else {
        setError('Failed to sign in with Microsoft. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src="/textLogo.png" alt="SyllySync" style={{ height: '48px', marginBottom: '16px' }} />
          <p>Sign in to access the admin dashboard</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="login-buttons">
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="auth-button google-button"
          >
            <svg className="auth-icon" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {loading ? 'Signing in...' : 'Sign in with Google'}
          </button>

          <button
            onClick={handleMicrosoftSignIn}
            disabled={loading}
            className="auth-button microsoft-button"
          >
            <svg className="auth-icon" viewBox="0 0 23 23">
              <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
              <path fill="#f35325" d="M1 1h10v10H1z"/>
              <path fill="#81bc06" d="M12 1h10v10H12z"/>
              <path fill="#05a6f0" d="M1 12h10v10H1z"/>
              <path fill="#ffba08" d="M12 12h10v10H12z"/>
            </svg>
            {loading ? 'Signing in...' : 'Sign in with Microsoft'}
          </button>
        </div>

        <div className="login-footer">
          <p>Admin access only</p>
          <p className="login-note">.edu and @syllysyncdev.com accounts can create organizations; @admin.syllysync.com has full admin access</p>
        </div>
      </div>

      {showOrgTypeDialog && (
        <OrgTypeDialog
          onSelect={handleOrgTypeSelection}
        />
      )}

      {showOrgRequestDialog && (
        <OrgRequestDialog
          onSubmit={handleOrgDetailsSubmit}
          onCancel={handleOrgDetailsCancel}
          orgType={pendingOrgType}
        />
      )}

      {showEduDomainDialog && (
        <EduDomainDialog
          onSubmit={handleEduDomainSubmit}
          initialDomain={pendingUniversityDomain || ''}
          onCancel={() => {
            setShowEduDomainDialog(false);
            setPendingEmail(null);
            setPendingUserId(null);
          }}
        />
      )}

      {showOrgPicker && (
        <OrgPickerDialog
          organizations={accessibleOrgs}
          onSelect={handleOrgPickerSelect}
        />
      )}

      {showUniversityExistsDialog && (
        <UniversityExistsDialog
          universityName={pendingUniversityDomain}
          onCreateOnCampus={handleCreateOnCampusUnderUniversity}
          onRequestAccess={handleRequestAccessToUniversity}
          onCancel={() => {
            setShowUniversityExistsDialog(false);
            setPendingEmail(null);
            setPendingUserId(null);
            setPendingUniversityDomain(null);
          }}
        />
      )}
    </div>
  );
};

export default Login;
